import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    QueryList,
    ViewChildren,
    inject,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OidcAuthService } from 'baseflow-auth';
import { debounce, firstValueFrom, of, switchMap, timer } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { SpinnerComponent, fade } from 'shared';

import { IWizardForm, IWizardStep, TWizardStepId } from '../../interfaces';
import { AppSettingsService, WizardService } from '../../services';
import {
    WizardStepBaseComponent,
    WizardStepCreateAccountComponent,
    WizardStepLoggedInComponent,
    WizardStepLoginComponent,
    WizardStepProfileComponent,
    WizardStepTenantInviteComponent,
    WizardStepTenantInviteSuccessComponent,
    WizardStepUpdateSuccessComponent,
} from '../wizard-step';

@Component({
    selector: 'app-user-portal-wizard',
    templateUrl: './wizard.component.html',
    styleUrls: ['./wizard.component.scss'],
    animations: [fade()],
    imports: [
        WizardStepCreateAccountComponent,
        WizardStepLoginComponent,
        WizardStepProfileComponent,
        WizardStepTenantInviteComponent,
        WizardStepTenantInviteSuccessComponent,
        WizardStepLoggedInComponent,
        WizardStepUpdateSuccessComponent,
        SpinnerComponent,
        TranslatePipe,
    ],
})
export class WizardComponent implements AfterViewInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    public backLinkUrl: string;
    public nextDisabled = false;

    protected goToPage: EventEmitter<TWizardStepId | number> = new EventEmitter();

    @ViewChildren(WizardStepBaseComponent)
    protected steps?: QueryList<WizardStepBaseComponent>;

    public wizardSteps: Array<IWizardStep> = [
        { id: 'account', image: 'page_1.svg', nextStepId: 'login' },
        { id: 'login', image: 'page_1.svg', nextStepId: 'logged-in' },
        { id: 'profile', image: 'page_1.svg', nextStepId: 'update-success' },
        { id: 'tenant-invite', image: 'page_2.svg', nextStepId: 'tenant-invite-success' },
        { id: 'tenant-invite-success', image: 'page_2.svg', nextStepId: 'logged-in' },
        { id: 'update-success', image: 'page_4.png', nextStepId: 'logged-in' },
        { id: 'logged-in', image: 'page_4.png' },
    ];

    public formData: IWizardForm = {} as IWizardForm;
    public currentStep: number;

    public get step(): IWizardStep {
        return this.wizardSteps[this.currentStep];
    }

    public constructor() {
        const appSettings = inject(AppSettingsService);
        const wizardService = inject(WizardService);
        const oidcAuthService = inject(OidcAuthService);

        appSettings.get(tap((settings) => (this.backLinkUrl = settings.url.backLink))).subscribe();

        this.goToPage
            .pipe(
                debounce(() => timer(100)),
                tap((pageId) => {
                    this._goToPage(pageId);
                })
            )
            .subscribe();

        this.currentStep = this.findPageIndex(oidcAuthService.isAuthenticated() ? 'logged-in' : 'account');
        wizardService.initialize(this.formData, this.goToPage, this.nextStep.bind(this));
    }

    public ngAfterViewInit(): void {
        this.stepComponent.show();

        // The active step (and its label) is only resolved here via @ViewChildren, after the view was
        // first checked. Re-run change detection so the bindings settle before the dev-mode
        // verification pass, avoiding NG0100 ExpressionChangedAfterItHasBeenCheckedError.
        this.changeDetectorRef.detectChanges();
    }

    public nextStep(): void {
        this.nextDisabled = true;

        (this.stepComponent.validateAndSubmitForm() ?? of(true))
            .pipe(
                take(1),
                switchMap(async (isValid) => {
                    if (isValid) {
                        if (this.step.nextStepId) return this._goToPage(this.step.nextStepId);
                        if (this.currentStep < this.wizardSteps.length - 1)
                            return this.goToPageByIndex(this.currentStep + 1 + (this.step.skipNextSteps || 0));
                    }
                    this.nextDisabled = false;
                })
            )
            .subscribe();
    }

    protected get stepComponent(): WizardStepBaseComponent {
        return this.steps?.find((s) => s.id === this.step.id);
    }

    private async _goToPage(id: TWizardStepId | number): Promise<void> {
        return this.goToPageByIndex(this.findPageIndex(id));
    }

    private findPageIndex(id: TWizardStepId | number): number {
        return typeof id === 'number' ? id : this.wizardSteps.findIndex((s) => s.id === id);
    }

    private async goToPageByIndex(index: number): Promise<void> {
        let component = this.stepComponent;
        const onBeforeHideResult = component.onBeforeHide?.();
        if (onBeforeHideResult) await firstValueFrom(onBeforeHideResult);
        component.hide();

        this.currentStep = index;

        component = this.stepComponent;
        const onBeforeShowResult = component.onBeforeShow?.();
        if (onBeforeShowResult) await firstValueFrom(onBeforeShowResult);
        component.show();
        this.nextDisabled = false;
    }
}
